import { useEffect, useState, type ReactNode } from "react";
import "./skeleton.css";

/**
 * Renders the real screen in skeleton mode while `loading`: the same layout, sections, cards, rows and
 * controls, with their content swapped for shimmering blocks of the same size. Because the skeleton IS
 * the screen, changing the screen changes its skeleton too. Pass placeholder data while loading so
 * lists render their usual number of rows.
 *
 * The skeleton only appears after `delay` ms (no flash on fast responses), is hidden from assistive
 * tech (aria-busy + a live "Carregando" label) and cannot be focused or clicked (inert).
 */
export function SkeletonScope({
  loading,
  label = "Carregando",
  delay = 150,
  children,
}: {
  loading: boolean;
  label?: string;
  delay?: number;
  children: ReactNode;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!loading) return setVisible(false);
    const timer = window.setTimeout(() => setVisible(true), delay);
    return () => window.clearTimeout(timer);
  }, [loading, delay]);

  return (
    <div className="sk-scope" data-loading={loading || undefined} data-visible={visible || undefined} aria-busy={loading} inert={loading}>
      {loading && (
        <span className="sr-only" role="status">
          {label}
        </span>
      )}
      <div aria-hidden={loading || undefined}>{children}</div>
    </div>
  );
}

/** Same length text while loading, so a placeholder value keeps the real width (e.g. a name or a date). */
export function placeholderText(length: number) {
  return " ".repeat(length);
}
